<template>
	<div
		v-click-outside="closeContextMenu"
		class="context-menu"
		:class="{ 'hide-bottom-border': hideBottomBorder, 'open': isMobileWebApp || isOpen }"
		@click.stop="toggleContextMenu"
		@mouseleave="handleMouseLeave"
	>
		<div
			class="context-menu-toggle"
		>
			<button
				type="button"
				class="toggle"
				aria-expanded="true"
			>
				<slot name="toggle" />
			</button>
		</div>
		<div
			v-if="!launcher"
			ref="context-menu-list"
			class="context-menu-list"
			:style="contextMenuListStyle"
			@mouseleave="closeContextMenu"
		>
			<!-- Place it in DOM only when opened -->
			<template v-if="isOpen">
				<ContextMenuItem
					v-for="(item, idx) in menuItems"
					:id="item.id"
					:key="`${$vnode.key}${item.id}${idx}`"
					:action="item.action"
					:disabled="item.disabled"
					:link="item.link"
					:label="item.label"
					:icon="item.icon"
					:hide-icon="hideIcon"
					:class="item.class"
					:selected="isSelected(item.id)"
					:value="value"
					v-on="$listeners"
					@click="handleMenuItemClick"
					@locked="locked = true"
					@unlocked="locked = false"
				>
					<template #arrow>
						<div
							v-if="showArrow"
							v-show="idx === arrowPosIdx"
							class="arrow"
							:style="arrowStyle"
						/>
					</template>
				</ContextMenuItem>
			</template>
		</div>
	</div>
</template>

<script>
import ContextMenuItem from './ContextMenuItem.vue';
import MenuItemHandlerMixin from './MenuItemHandlerMixin';
import { mapGetters } from 'vuex';
export default {
	name:       'ContextMenu',
	components: { ContextMenuItem },
	mixins:     [MenuItemHandlerMixin],
	props:      {
		menuItems: {
			type:    Array,
			default: () => [],
		},
		launcher: {
			type:    Function,
			default: null,
		},
		selected: {
			default: null,
		},
		value: {
			default: null,
		},
		hideIcon: {
			type: Boolean,
		},
		showArrow: {
			type:    Boolean,
			default: true,
		},
		alignMenuLeftOnToggle: {
			type:    Boolean,
			default: true,
		},
		// vertical offset of the context menu relative to the toggle button
		menuOffset: {
			type:    Number,
			default: 0,
		},
	},
	data() {
		return {
			arrowOffset:             27,
			isOpen:                  false,
			contextMenuListPosition: {
				left: 0,
				top:  0,
			},
			arrowStyle:       {},
			hideBottomBorder: false,
			direction:        null,
			locked:           false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		contextMenuListStyle() {
			if (this.isOpen) {
				return {
					display:   'block',
					transform: 'translateY(0);',
					opacity:   '1',
					...this.contextMenuListPosition,
				};
			}
			return {
				opacity: '0',
				display: 'none',
			};
		},
		arrowPosIdx() {
			if (this.direction === 'bottom-right') {
				return 0;
			}
			// top-right
			return this.menuItems.length - 1;
		},
		floatingMenuRef() {
			return this.$refs['context-menu-list'];
		},
	},
	watch: {
		isOpen(val) {
			this.$emit('openStatusChanged', val);
			if (val === true) {
				if (this.launcher) {
					this.launcher(this.menuItems);
					return;
				}
				this.$nextTick(() => {
					const elPos = this.getListElPosition();
					this.contextMenuListPosition = {
						left: `${elPos[0] + 11}px`,
						top:  `${elPos[1] + 4}px`,
					};
				});
			}
		},
		locked(val) {
			if (val) {
				this.isOpen = true;
			}
		},
	},
	beforeDestroy() {
		this.removeFloatingMenuRef();
	},
	methods: {
		isSelected(itemId) {
			return this.selected === itemId;
		},
		removeFloatingMenuRef() {
			if (this.floatingMenuRef) {
				this.floatingMenuRef.remove();
			}
		},
		toggleContextMenu() {
			this.isOpen = !this.isOpen;
			if (this.isOpen) {
				if (!this.launcher) {
					this.$root.$el.append(this.floatingMenuRef);
				}
			} else {
				this.removeFloatingMenuRef();
			}
		},
		getListElPosition() {
			const listEl = this.floatingMenuRef; // List (dropdown) element

			const toggleElChild = this.$el.querySelector('.toggle').children[0]; // Toggle list button
			if (!(listEl && toggleElChild)) {
				return [0, 0];
			}

			const listRect = listEl.getBoundingClientRect(); // List size/position
			const toggleElChildRect = toggleElChild.getBoundingClientRect(); // Toggle button size/position

			// Calculate how far the menu can render:
			// Toggle button bottom + List height + Arrow height (~14px)
			const arrowHeight = 14;
			const positionLimit = toggleElChildRect.bottom + listRect.height + arrowHeight;

			// Variable for top location
			let top;

			// Set arrow defaults
			this.arrowStyle = {
				left: `${listEl.clientWidth - this.arrowOffset}px`,
			};

			// Check if the list exceeds the bottom limit of parent container
			const parentRect = this.floatingMenuRef.parentElement.getBoundingClientRect(); // Parent container size/position
			if (positionLimit > parentRect.bottom) {
				// Render list to top direction
				this.direction = 'top-right';
				top = toggleElChildRect.top - listRect.height - 3.5 - this.menuOffset;
				this.arrowStyle.top = `${-7 + listRect.height}px`;
			} else {
				// Render list to bottom direction
				this.direction = 'bottom-right';
				top = toggleElChildRect.bottom + this.menuOffset;
				this.arrowStyle.top = '-7px';
			}

			// Calculate left location
			const rightOffset = 12;
			const widthOffset = this.alignMenuLeftOnToggle ? listRect.width : 0;
			const left = toggleElChildRect.left - widthOffset + rightOffset + 9;

			// Return position
			return [left, top];
		},
		handleMouseLeave() {
			setTimeout(() => {
				if (!this.locked) {
					this.closeContextMenu();
				}
			}, 100);
		},
		closeContextMenu() {
			this.isOpen = false;
			this.removeFloatingMenuRef();
		},
		close() {
			this.closeContextMenu();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$arrow-size: 14px;

.context-menu {
	line-height: 0;

	&.open {
		display: block;
	}
}

.context-menu::v-deep.hide-bottom-border {
	.menu-item:last-of-type .body {
		border-bottom: none;
	}
}

.context-menu-list {
	box-shadow: 3px 5px 10px 2px rgb(var(--color-rgb-black), 40%);
	min-width: 165px;
	position: fixed;
	z-index: 6000;
	animation: fadeInFromNone 0.2s ease-out;
}

.context-menu-list::v-deep {
	.arrow {
		position: absolute;
		width: 14px;
		height: 14px;
		z-index: -1;
		transform: rotate(45deg);
		background: var(--color-grey-850);
		display: block;
		box-shadow: -1px -1px 2px 0 rgba(var(--color-rgb-shadow-grey), 0.15);
	}

	.menu-item:hover .arrow {
		background: var(--color-grey-750);
	}
}

</style>
